import { useId } from 'react';

import type { PermissionKey, UserRole } from './contractsMirror.js';
import { summarizeRole, TOTAL_PERMISSIONS } from './roleSummary.js';

/**
 * The primary access surface: pick ONE role for one person.
 *
 * This replaces a 19-column × N-user toggle matrix. The matrix grew in both directions
 * (a 36th permission widened every row; a 50th employee added another column) and put 35
 * independent security decisions in front of an owner who, in practice, makes one: which
 * of three roles this person is. Production bears that out — across 50 users there are
 * exactly 2 override rows, both on the same person.
 *
 * So overrides did not go away; they stopped being the front door. They live behind the
 * Advanced row, which shows a count so genuine drift is still visible at a glance.
 *
 * Native `<input type="radio">` inside a label, not divs with click handlers: arrow-key
 * navigation, the roving tab stop and the announced group name all come free and are very
 * easy to get wrong by hand on a privilege-granting screen.
 */

interface RoleCardsProps {
  /** Server-computed preset per role (`GET /api/v1/users` → `rolePresets`). */
  rolePresets: Record<UserRole, PermissionKey[]>;
  /** The person's CURRENT role, as last returned by the server. */
  current: UserRole;
  /** Roles offered as cards. The caller decides which; Admin is never among them. */
  roles: readonly UserRole[];
  onSelect: (role: UserRole) => void;
  disabled: boolean;
  t: (key: string, opts?: Record<string, unknown>) => string;
}

export function RoleCards({ rolePresets, current, roles, onSelect, disabled, t }: RoleCardsProps) {
  const groupName = useId();

  return (
    <fieldset className="min-w-0" disabled={disabled}>
      <legend className="mb-2 font-medium text-foreground text-sm">
        {t('users:access.roleLegend')}
      </legend>

      {/* Explicit `grid-cols-1` rather than leaning on the tokens.css guard for
          count-less grids: a card's own min-content width would otherwise set the
          track floor and push the page sideways on a phone. */}
      <div className="grid grid-cols-1 gap-2">
        {roles.map((role) => {
          const preset = rolePresets[role] ?? [];
          const checked = role === current;
          return (
            <label
              key={role}
              // The selected card is marked by a real border + a tonal surface, not a glow
              // or a drop shadow. `has-[:focus-visible]` puts the focus ring on the CARD so
              // keyboard users see the whole target, not a 16px dot inside it.
              className={`flex cursor-pointer gap-3 rounded-md border p-4 transition-colors has-[:focus-visible]:outline-none has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring ${
                checked ? 'border-primary bg-primary/5' : 'border-border bg-card hover:bg-accent/40'
              }`}
            >
              <input
                type="radio"
                name={groupName}
                value={role}
                checked={checked}
                onChange={() => onSelect(role)}
                className="mt-1 size-4 shrink-0 accent-primary"
              />
              <span className="min-w-0 flex-1">
                <span className="block font-semibold text-foreground text-sm">
                  {t(`users:roles.${role}`)}
                </span>
                <span className="mt-0.5 block text-muted-foreground text-sm">
                  {t(`users:access.roleDescription.${role}`)}
                </span>
                <span className="mt-1.5 block font-mono text-muted-foreground text-xs">
                  {t('users:access.permissionCount', {
                    granted: preset.length,
                    total: TOTAL_PERMISSIONS,
                  })}
                </span>
              </span>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

/**
 * The read-only "what does this role actually grant" panel, 8 grouped rows rather than 35
 * switches. Coverage is TEXT (`Full` / `Partial` / `None`), never colour alone, so it
 * survives greyscale and colour-blindness (A11Y-01).
 */
export function RoleGrantSummary({
  preset,
  t,
}: {
  preset: readonly PermissionKey[];
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const rows = summarizeRole(preset);

  return (
    <div className="min-w-0 rounded-md border border-border bg-card">
      <h3 className="border-border border-b px-4 py-3 font-semibold text-foreground text-sm">
        {t('users:access.grantsHeading')}
      </h3>
      <ul className="divide-y divide-border/60">
        {rows.map((row) => (
          <li key={row.label} className="flex items-center gap-3 px-4 py-2.5">
            <span className="min-w-0 flex-1 text-foreground text-sm">
              {t(`users:permissions.groups.${row.label}`)}
            </span>
            <span
              className={`shrink-0 text-xs ${
                row.coverage === 'none' ? 'text-muted-foreground' : 'text-foreground'
              }`}
            >
              {row.coverage === 'partial'
                ? t('users:access.coverage.partial', { granted: row.granted, total: row.total })
                : t(`users:access.coverage.${row.coverage}`)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
